import { useEffect, useState } from "react";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { listBusinessPractitioners } from "@/features/business-practitioners/api";
import { useFirmContextStore } from "@/features/firm-context/store/firm-context-store";

/**
 * Resolves the "which firm am I acting in" context from the active-firm store
 * (set by the sidebar firm switcher or a BP-directory drill-in), so a house
 * Team Leader / staffer — or a GA/SA — can default to the in-house (CAP) book
 * and switch into a Business Practitioner's workspace to manage it on their
 * behalf.
 *
 * The context lives in an in-memory store — not the URL — so it persists across
 * page navigation (a workspace switch, not a per-page drill-in) and resets to
 * In-house on a full reload.
 */
export interface FirmContext {
  /** Firm id to send to the client/practitioner/new-arrival APIs, or undefined
   *  to let the backend scope normally (a BP member's own firm). */
  firm?: string;
  /** Set only when acting on a firm *other* than the user's house (CAP) book —
   *  i.e. a real drill-in. Drives whether the banner shows. */
  actingFirmId?: string;
  /** Display name of the acting firm, once known. */
  actingFirmName?: string;
}

export function useFirmContext(): FirmContext {
  const user = useAuthStore((s) => s.user);
  const activeFirmId = useFirmContextStore((s) => s.activeFirmId);
  const activeFirmName = useFirmContextStore((s) => s.activeFirmName);

  const houseFirmId = user?.firm?.org_type === "house" ? user.firm.id : undefined;

  // House users (CD/TL/staff) and GA/SA default to the house (CAP) book so the
  // plain page never commingles every firm's clients; an active firm overrides
  // it. A BP member has no house book, so they fall through to backend scoping.
  const firm = activeFirmId ?? houseFirmId;

  // Acting-on-behalf only applies to a firm that isn't the user's own house book.
  const actingFirmId = activeFirmId && activeFirmId !== houseFirmId ? activeFirmId : undefined;

  // The name is normally set alongside the id when switching; if a firm was set
  // without one, fall back to a directory lookup.
  const [lookupName, setLookupName] = useState<string>();
  const needsLookup = Boolean(actingFirmId) && !activeFirmName;

  useEffect(() => {
    if (!needsLookup) {
      setLookupName(undefined);
      return;
    }
    let active = true;
    listBusinessPractitioners()
      .then((bps) => active && setLookupName(bps.find((b) => b.id === actingFirmId)?.name))
      .catch(() => active && setLookupName(undefined));
    return () => {
      active = false;
    };
  }, [needsLookup, actingFirmId]);

  return {
    firm,
    actingFirmId,
    actingFirmName: actingFirmId ? (activeFirmName ?? lookupName) : undefined,
  };
}
